import React, { useMemo } from 'react';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../general-components/icon';

const WINDOW_SIZE = 6;

interface PaginationControlsProps {
	isLoading: boolean;
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}

export default function PaginationControls(props: PaginationControlsProps) {
	const {
		isLoading,
		currentPage,
		totalPages,
		onPageChange,
	} = props;

	const showFirstThree = useMemo(() => currentPage <= 3, [currentPage]);
	const showLastThree = useMemo(() => currentPage >= totalPages - 3, [currentPage, totalPages]);

	const numberButtons = useMemo(() => {
		let startPage = Math.max(1, currentPage - 3);
		let endPage = Math.min(totalPages, currentPage + 2);

		// first 3 pages
		if (showFirstThree) {
			startPage = 1;
			endPage = WINDOW_SIZE;
		}

		// last 3 pages
		if (showLastThree) {
			startPage = Math.max(1, totalPages - WINDOW_SIZE);
			endPage = totalPages;
		}

		const elements = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
		return (
			<>
				{elements.map((page) => (
					<div
						key={page}
						className={classNames('sc-pagination-btn', currentPage === page && 'selected')}
						onClick={() => onPageChange(page)}
						aria-disabled={isLoading}
					>
						<span className="sc-pagination-number">{page}</span>
					</div>
				))}
			</>
		);
	}, [currentPage, totalPages, showFirstThree, showLastThree, onPageChange]);

	return totalPages > 1 ? (
		<div className={classNames('sc-pagination-controls', isLoading && 'searching')}>
			<div className="sc-shadow pagination" />
			{currentPage > 1 && (
				<div className="sc-pagination-btn" onClick={() => onPageChange(currentPage - 1)}>
					<Icon name={ICONS.ARROW_LEFT} size={12} color={COLORS.TEXT_GRAY_300} />
				</div>
			)}
			{numberButtons}
			{!showLastThree && (
				<>
					<div className="sc-pagination-btn ellipses">
						<Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={12} color={COLORS.TEXT_GRAY_300} />
					</div>
					<div className="sc-pagination-btn" onClick={() => onPageChange(totalPages)}>
						<span className="sc-pagination-number">{totalPages}</span>
					</div>
				</>
			)}
			{currentPage < totalPages && (
				<div className="sc-pagination-btn" onClick={() => onPageChange(currentPage + 1)}>
					<Icon name={ICONS.ARROW_RIGHT} size={12} color={COLORS.TEXT_GRAY_300} />
				</div>
			)}
		</div>
	) : (
		<div className="sc-shadow" />
	);
}
